import React, { useState } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import TabsContext from '../context/tabs-context';
import TabsLayout from '../components/layout/tabs-layout';
import { APPEARANCES } from '../constants';

const subscribers = new Set();
const activeTabManager = {
  subscribe: (callback) => {
    if (subscribers.has(callback)) return;

    subscribers.add(callback);
  },
  publish: (data) => {
    subscribers.forEach((callback) => callback(data));
  },
  unsubscribe: (callback) => {
    if (!subscribers.has(callback)) return;

    subscribers.delete(callback);
  },
};

export default function TabsContainer({
  defaultActiveTabId = '',
  activeTabId = '',
  appearance = APPEARANCES.bold,
  onTabChange = Function.prototype,
  disableSwipeableContent = false,
  skipRenderInactiveContent = false,
  scrollContainerRef = null,
  noBackground = false,
  noSideSpacing = false,
  noVerticalSpacing = false,
  noOverflow = false,
  children,
}) {
  if ((defaultActiveTabId && activeTabId) || (!defaultActiveTabId && !activeTabId))
    throw new Error(`
    Tabs component can be either controlled, or uncontrolled.
    If you want it to be controlled - please pass the 'activeTabId',
    and update it externally via the 'onTabChange' callback.
    If you want it to be uncontrolled - please pass the 'defaultActiveTabId',
    and its updates will be handled by the Tabs component itself.
  `);

  const [isSwipePrevented, setSwipePrevented] = useState(false);
  const isUncontrolled = defaultActiveTabId && !activeTabId;

  const [uncontrolledActiveTabId, setUncontrolledActiveTabId] = useState(defaultActiveTabId);
  const onUncontrolledTabChange = (tabId) => {
    setUncontrolledActiveTabId(tabId);

    // Useful to have this callback in the uncontrolled mode also,
    // for an analytics calls, for example.
    onTabChange(tabId);
  };

  return (
    <TabsContext.Provider
      value={{
        appearance,
        noBackground,
        noSideSpacing,
        noVerticalSpacing,
        noOverflow,
        activeTabId: isUncontrolled ? uncontrolledActiveTabId : activeTabId,
        onTabChange: isUncontrolled ? onUncontrolledTabChange : onTabChange,
        activeTabManager,
        disableSwipeableContent: disableSwipeableContent || bowser.desktop,
        skipRenderInactiveContent,
        isSwipePrevented,
        setSwipePrevented,
        scrollContainerRef,
      }}
    >
      <TabsLayout>{children}</TabsLayout>
    </TabsContext.Provider>
  );
}

TabsContainer.propTypes = {
  children: PropTypes.node.isRequired,
  defaultActiveTabId: PropTypes.string,
  activeTabId: PropTypes.string,
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
  onTabChange: PropTypes.func,
  disableSwipeableContent: PropTypes.bool,
  skipRenderInactiveContent: PropTypes.bool,
  noBackground: PropTypes.bool,
  noSideSpacing: PropTypes.bool,
  noVerticalSpacing: PropTypes.bool,
  noOverflow: PropTypes.bool,
  scrollContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};
